// Les abonnements en cours, un par un : la ligne « Abonnements » du budget en est toujours le total.
import { STARTING_SUBSCRIPTIONS, SUBSCRIPTIONS } from '../content/subscriptions';
import { round2 } from '../engine/finance';
import type { GameState, Subscription, SubscriptionId } from '../engine/state';

export function subscriptionsTotal(state: GameState): number {
  return round2(state.subscriptions.reduce((sum, sub) => sum + sub.amount, 0));
}

/** Remet la ligne « Abonnements » au total des abonnements en cours. */
function syncExpense(state: GameState): void {
  const amount = subscriptionsTotal(state);
  const expense = state.expenses.abonnements;
  if (expense) expense.amount = amount;
  else state.expenses.abonnements = { amount, examined: 0, optimized: false };
}

/** Les abonnements du départ, au premier budget. */
export function startSubscriptions(state: GameState): void {
  state.subscriptions = STARTING_SUBSCRIPTIONS.map((sub) => ({ ...sub }));
  syncExpense(state);
}

/** Le dernier abonnement pris de ce genre, s'il est en cours. */
export function findSubscription(state: GameState, id: SubscriptionId): Subscription | undefined {
  return [...state.subscriptions].reverse().find((sub) => sub.id === id);
}

export function hasSubscription(state: GameState, id: SubscriptionId): boolean {
  return findSubscription(state, id) !== undefined;
}

export function subscribe(
  state: GameState,
  id: SubscriptionId,
  amount: number,
  extra: Pick<Subscription, 'shared'> = {},
): void {
  state.subscriptions = [...state.subscriptions, { id, amount: round2(Math.max(0, amount)), ...extra }];
  syncExpense(state);
}

/** Résilie le dernier abonnement de ce genre ; renvoie ce qu'il coûtait par mois (0 s'il n'y en avait pas). */
export function unsubscribe(state: GameState, id: SubscriptionId): number {
  const target = findSubscription(state, id);
  if (!target) return 0;
  state.subscriptions = state.subscriptions.filter((sub) => sub !== target);
  syncExpense(state);
  return target.amount;
}

/** Le prix d'un abonnement change : une hausse, ou le passage à l'offre avec publicités. */
export function changeSubscription(state: GameState, id: SubscriptionId, delta: number, withAds?: boolean): void {
  const target = findSubscription(state, id);
  if (!target) return;
  target.amount = round2(Math.max(0, target.amount + delta));
  if (withAds !== undefined) target.withAds = withAds;
  syncExpense(state);
}

/** Ce que coûtent les abonnements qu'on n'utilise plus. */
export function forgottenTotal(state: GameState): number {
  return round2(state.subscriptions.filter((sub) => sub.forgotten).reduce((sum, sub) => sum + sub.amount, 0));
}

/** Résilie les abonnements qu'on n'utilise plus ; renvoie l'économie par mois. */
export function cancelForgotten(state: GameState): number {
  const saved = forgottenTotal(state);
  state.subscriptions = state.subscriptions.filter((sub) => !sub.forgotten);
  syncExpense(state);
  return saved;
}

/** Au bilan annuel, chaque abonnement suit la hausse des prix. */
export function indexSubscriptions(state: GameState, rate: number): void {
  for (const sub of state.subscriptions) sub.amount = round2(sub.amount * (1 + rate));
  syncExpense(state);
}

/** La plateforme de séries que visent les hausses de prix : la plus chère. */
export function streamingSubscription(state: GameState): Subscription | undefined {
  return state.subscriptions
    .filter((sub) => SUBSCRIPTIONS[sub.id].streaming)
    .reduce<Subscription | undefined>((best, sub) => (!best || sub.amount > best.amount ? sub : best), undefined);
}
